import { useState, useMemo, useEffect } from 'react';
import { Eye, EyeOff, GripVertical, Trash2, Plus, ArrowUp, ArrowDown, Check, RefreshCw } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useDesignSpec } from '../../hooks/useDesignSpec';
import { generateMockupFromDesignSpec } from '../../lib/designSpecEngine';
import { Select, TextInput } from '../ui/FormField';
import { ColorPicker } from '../ui/ColorPicker';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import type {
  DesignStyle,
  ColorScheme,
  FontPair,
  LayoutStyle,
  SectionType,
  SectionSpec,
} from '../../types/design-spec';

const DESIGN_STYLES: { value: DesignStyle; label: string }[] = [
  { value: 'modern-minimalist', label: 'Modern Minimalist' },
  { value: 'corporate-professional', label: 'Corporate Professional' },
  { value: 'creative-bold', label: 'Creative Bold' },
  { value: 'elegant-luxury', label: 'Elegant Luxury' },
  { value: 'tech-startup', label: 'Tech Startup' },
  { value: 'warm-friendly', label: 'Warm Friendly' },
  { value: 'medical-trusted', label: 'Medical Trusted' },
  { value: 'portfolio-artistic', label: 'Portfolio Artistic' },
  { value: 'educational-clean', label: 'Educational Clean' },
  { value: 'custom', label: 'Custom' },
];

const COLOR_SCHEMES: { value: ColorScheme; label: string }[] = [
  { value: 'blue-professional', label: 'Blue Professional' },
  { value: 'green-nature', label: 'Green Nature' },
  { value: 'red-energetic', label: 'Red Energetic' },
  { value: 'black-luxury', label: 'Black Luxury' },
  { value: 'warm-neutral', label: 'Warm Neutral' },
  { value: 'high-contrast', label: 'High Contrast' },
  { value: 'pastel-soft', label: 'Pastel Soft' },
  { value: 'custom', label: 'Custom' },
];

const FONT_PAIRS: { value: FontPair; label: string }[] = [
  { value: 'inter-noto', label: 'Inter + Noto Sans TC' },
  { value: 'playfair-inter', label: 'Playfair Display + Inter' },
  { value: 'montserrat-open', label: 'Montserrat + Open Sans' },
  { value: 'noto-tc-only', label: 'Noto Sans TC' },
  { value: 'system', label: 'System Fonts' },
  { value: 'custom', label: 'Custom' },
];

const LAYOUT_STYLES: { value: LayoutStyle; label: string }[] = [
  { value: 'centered-single', label: 'Centered Single' },
  { value: 'wide-multi', label: 'Wide Multi' },
  { value: 'asymmetric', label: 'Asymmetric' },
  { value: 'card-grid', label: 'Card Grid' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'split', label: 'Split' },
  { value: 'custom', label: 'Custom' },
];

const SECTION_TYPES: { value: SectionType; label: string }[] = [
  { value: 'hero', label: 'Hero' },
  { value: 'features', label: 'Features' },
  { value: 'services', label: 'Services' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'testimonials', label: 'Testimonials' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'team', label: 'Team' },
  { value: 'about', label: 'About' },
  { value: 'contact', label: 'Contact' },
  { value: 'cta', label: 'CTA' },
  { value: 'faq', label: 'FAQ' },
  { value: 'blog', label: 'Blog' },
  { value: 'stats', label: 'Stats' },
  { value: 'process', label: 'Process' },
  { value: 'trust-logos', label: 'Trust Logos' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'footer', label: 'Footer' },
];

export function DesignSpecEditor() {
  const config = useProjectStore((state) => state.config);
  const generateMockup = useProjectStore((state) => state.generateMockup);
  const setActiveTab = useProjectStore((state) => state.setActiveTab);
  const isGenerating = useProjectStore((state) => state.isGenerating);

  const {
    designSpec,
    ensureDesignSpec,
    setStyle,
    setColorScheme,
    setFontPair,
    setLayoutStyle,
    updateColors,
    updateTypography,
    updateLayout,
    updateSection,
    addSection,
    removeSection,
    moveSection,
    toggleSectionVisibility,
  } = useDesignSpec();

  const [newSectionType, setNewSectionType] = useState<SectionType>('features');
  const [previewKey, setPreviewKey] = useState(0);

  const effectiveDesignSpec = designSpec ?? ensureDesignSpec();

  const mockupHtml = useMemo(() => {
    return generateMockupFromDesignSpec(effectiveDesignSpec, config);
  }, [effectiveDesignSpec, config]);

  useEffect(() => {
    setPreviewKey((k) => k + 1);
  }, [mockupHtml]);

  const handleApply = () => {
    generateMockup();
    setActiveTab('mockup');
  };

  if (!config.designSpec) {
    ensureDesignSpec();
  }

  const sortedSections = [...effectiveDesignSpec.sections].sort((a, b) => a.order - b.order);

  return (
    <div className="max-w-7xl mx-auto pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Design Spec 編輯器</h2>
        <button
          type="button"
          onClick={handleApply}
          disabled={isGenerating}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold transition-colors"
        >
          {isGenerating ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
          套用並預覽 Mockup
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <CollapsibleSection title="風格與排版" defaultOpen>
            <Select
              label="設計風格"
              value={effectiveDesignSpec.style}
              onChange={(e) => setStyle(e.target.value as DesignStyle)}
              options={DESIGN_STYLES}
            />
            <Select
              label="配色方案"
              value={effectiveDesignSpec.colorScheme || 'custom'}
              onChange={(e) => setColorScheme(e.target.value as ColorScheme)}
              options={COLOR_SCHEMES}
            />
            <Select
              label="字體組合"
              value={effectiveDesignSpec.fontPair || 'system'}
              onChange={(e) => setFontPair(e.target.value as FontPair)}
              options={FONT_PAIRS}
            />
            <Select
              label="佈局風格"
              value={effectiveDesignSpec.layoutStyle || 'custom'}
              onChange={(e) => setLayoutStyle(e.target.value as LayoutStyle)}
              options={LAYOUT_STYLES}
            />
          </CollapsibleSection>

          <CollapsibleSection title="顏色" defaultOpen>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ColorPicker label="主色" value={effectiveDesignSpec.colors.primary} onChange={(v) => updateColors({ primary: v })} required />
              <ColorPicker label="輔色" value={effectiveDesignSpec.colors.secondary} onChange={(v) => updateColors({ secondary: v })} required />
              <ColorPicker label="強調色" value={effectiveDesignSpec.colors.accent} onChange={(v) => updateColors({ accent: v })} required />
              <ColorPicker label="背景色" value={effectiveDesignSpec.colors.background} onChange={(v) => updateColors({ background: v })} required />
              <ColorPicker label="表面色" value={effectiveDesignSpec.colors.surface} onChange={(v) => updateColors({ surface: v })} required />
              <ColorPicker label="文字色" value={effectiveDesignSpec.colors.text} onChange={(v) => updateColors({ text: v })} required />
              <ColorPicker label="次要文字色" value={effectiveDesignSpec.colors.muted} onChange={(v) => updateColors({ muted: v })} required />
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">深色模式</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <ColorPicker label="深色背景" value={effectiveDesignSpec.colors.darkMode?.background || '#0f172a'} onChange={(v) => updateColors({ darkMode: { ...effectiveDesignSpec.colors.darkMode, background: v } })} required />
                <ColorPicker label="深色表面" value={effectiveDesignSpec.colors.darkMode?.surface || '#1e293b'} onChange={(v) => updateColors({ darkMode: { ...effectiveDesignSpec.colors.darkMode, surface: v } })} required />
                <ColorPicker label="深色文字" value={effectiveDesignSpec.colors.darkMode?.text || '#f8fafc'} onChange={(v) => updateColors({ darkMode: { ...effectiveDesignSpec.colors.darkMode, text: v } })} required />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="排版設定">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                label="標題字體"
                value={effectiveDesignSpec.typography.headingFont}
                onChange={(e) => updateTypography({ headingFont: e.target.value })}
              />
              <TextInput
                label="內文字體"
                value={effectiveDesignSpec.typography.bodyFont}
                onChange={(e) => updateTypography({ bodyFont: e.target.value })}
              />
              <TextInput
                label="基準字級"
                value={effectiveDesignSpec.typography.baseSize}
                onChange={(e) => updateTypography({ baseSize: e.target.value })}
              />
              <Select
                label="字級縮放"
                value={effectiveDesignSpec.typography.scale}
                onChange={(e) => updateTypography({ scale: e.target.value as DesignSpec['typography']['scale'] })}
                options={[
                  { value: 'small', label: 'Small' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'large', label: 'Large' },
                ]}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="佈局設定">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                label="最大寬度"
                value={effectiveDesignSpec.layout.maxWidth}
                onChange={(e) => updateLayout({ maxWidth: e.target.value })}
              />
              <TextInput
                label="欄數"
                type="number"
                value={String(effectiveDesignSpec.layout.gridColumns)}
                onChange={(e) => updateLayout({ gridColumns: Number(e.target.value) })}
              />
              <Select
                label="間距"
                value={effectiveDesignSpec.layout.spacing}
                onChange={(e) => updateLayout({ spacing: e.target.value as DesignSpec['layout']['spacing'] })}
                options={[
                  { value: 'compact', label: 'Compact' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'loose', label: 'Loose' },
                ]}
              />
              <TextInput
                label="容器內距"
                value={effectiveDesignSpec.layout.containerPadding}
                onChange={(e) => updateLayout({ containerPadding: e.target.value })}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="區塊管理" defaultOpen>
            <div className="flex gap-2 mb-4">
              <Select
                label=""
                value={newSectionType}
                onChange={(e) => setNewSectionType(e.target.value as SectionType)}
                options={SECTION_TYPES}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => addSection(newSectionType)}
                className="flex items-center gap-1 px-4 py-2 rounded-lg text-white"
                style={{ background: 'var(--pf-primary, #3b82f6)' }}
              >
                <Plus size={16} /> 新增
              </button>
            </div>

            <div className="space-y-2">
              {sortedSections.map((section, index) => (
                <SectionEditor
                  key={section.id}
                  section={section}
                  index={index}
                  total={sortedSections.length}
                  onChange={updateSection}
                  onRemove={removeSection}
                  onMove={moveSection}
                  onToggleVisibility={toggleSectionVisibility}
                />
              ))}
              {sortedSections.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">尚未新增任何區塊</p>
              )}
            </div>
          </CollapsibleSection>
        </div>

        <div className="flex flex-col h-[600px] lg:h-auto">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">即時預覽</h3>
          <div className="flex-1 bg-slate-100 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <iframe
              key={previewKey}
              title="Design Spec Preview"
              srcDoc={mockupHtml}
              className="w-full h-full min-h-[500px] bg-white"
              sandbox="allow-scripts"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface SectionEditorProps {
  section: SectionSpec;
  index: number;
  total: number;
  onChange: (section: SectionSpec) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onToggleVisibility: (id: string) => void;
}

function SectionEditor({ section, index, total, onChange, onRemove, onMove, onToggleVisibility }: SectionEditorProps) {
  return (
    <div className={`flex items-center gap-2 p-3 rounded-lg border ${section.visible ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700' : 'bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 opacity-60'}`}>
      <GripVertical size={16} className="text-slate-400" />
      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={section.name}
          onChange={(e) => onChange({ ...section, name: e.target.value })}
          className="w-full px-2 py-1 text-sm font-medium bg-transparent border-0 focus:ring-0 text-slate-900 dark:text-white"
        />
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <span className="uppercase tracking-wider">{section.type}</span>
          <Select
            label=""
            value={section.layout}
            onChange={(e) => onChange({ ...section, layout: e.target.value as SectionSpec['layout'] })}
            options={[
              { value: 'full-width', label: 'Full' },
              { value: 'contained', label: 'Contained' },
              { value: 'split', label: 'Split' },
              { value: 'grid', label: 'Grid' },
              { value: 'carousel', label: 'Carousel' },
            ]}
            className="text-xs py-1 px-2"
          />
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onMove(section.id, 'up')}
          disabled={index === 0}
          className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded disabled:opacity-30"
        >
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          onClick={() => onMove(section.id, 'down')}
          disabled={index === total - 1}
          className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded disabled:opacity-30"
        >
          <ArrowDown size={14} />
        </button>
        <button
          type="button"
          onClick={() => onToggleVisibility(section.id)}
          className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
        >
          {section.visible ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <button
          type="button"
          onClick={() => onRemove(section.id)}
          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
